import { Link } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  useRecommendations,
  type RecommendationContext,
  type RecommendationItem,
} from "@/hooks/useRecommendations";
import { cn } from "@/lib/utils";

interface CourseRecommendationsProps {
  context?: RecommendationContext;
  /** Anchor course ID (used when context = "course_detail"). */
  courseId?: string;
  /** Max items to render. */
  limit?: number;
  /** Card title — defaults vary by context. */
  title?: string;
  description?: string;
  /** "grid" for dashboard / detail page; "list" for narrow surfaces (cart drawer). */
  variant?: "grid" | "list";
  /** Optional callback fired on click (in addition to internal logging). */
  onItemClick?: (item: RecommendationItem) => void;
  className?: string;
}

const DEFAULT_TITLES: Record<RecommendationContext, { title: string; description: string }> = {
  dashboard: {
    title: "Recomendado para você",
    description: "Cursos selecionados com base nos seus interesses.",
  },
  course_detail: {
    title: "Você também pode gostar",
    description: "Cursos relacionados a este conteúdo.",
  },
  cart: {
    title: "Combine com",
    description: "Cursos que costumam ser comprados juntos.",
  },
  post_purchase: {
    title: "Continue aprendendo",
    description: "Próximos passos sugeridos para você.",
  },
};

const fmt = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

async function logRecommendationClick(item: RecommendationItem, context: RecommendationContext) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("user_activity").insert({
      user_id: user.id,
      activity_type: "recommendation_click",
      details: {
        context,
        course_id: item.course.id,
        course_slug: item.course.slug,
        score: item.score,
        timestamp: new Date().toISOString(),
      },
      action_severity: "low",
      risk_score: 0,
    });
  } catch (err) {
    console.error("[CourseRecommendations] click log failed", err);
  }
}

export function CourseRecommendations({
  context = "dashboard",
  courseId,
  limit = 6,
  title,
  description,
  variant = "grid",
  onItemClick,
  className,
}: CourseRecommendationsProps) {
  const { data, isLoading } = useRecommendations({ context, courseId, limit });
  const defaults = DEFAULT_TITLES[context];
  const finalTitle = title ?? defaults.title;
  const finalDescription = description ?? defaults.description;

  const items = data?.items ?? [];

  if (!isLoading && items.length === 0) return null;

  const handleClick = (item: RecommendationItem) => {
    void logRecommendationClick(item, context);
    onItemClick?.(item);
  };

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          {finalTitle}
        </CardTitle>
        <CardDescription>{finalDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : variant === "list" ? (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.course.id}>
                <Link
                  to={`/courses/${item.course.slug}`}
                  onClick={() => handleClick(item)}
                  className="flex gap-3 rounded-lg border border-border/60 bg-card/50 p-2 hover:bg-accent/40 transition-colors"
                >
                  <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
                    {item.course.thumbnail_url && (
                      <img
                        src={item.course.thumbnail_url}
                        alt={item.course.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.course.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{item.reason}</p>
                  </div>
                  <span className="self-center text-xs font-medium tabular-nums text-foreground/80">
                    {item.course.is_free
                      ? "Grátis"
                      : item.course.price
                        ? fmt(Number(item.course.price))
                        : "—"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((item) => (
              <Link
                key={item.course.id}
                to={`/courses/${item.course.slug}`}
                onClick={() => handleClick(item)}
                className="group flex flex-col rounded-lg border border-border/60 bg-card/50 overflow-hidden hover:border-primary/40 hover:shadow-sm transition-all"
              >
                <div className="aspect-video w-full overflow-hidden bg-muted">
                  {item.course.thumbnail_url && (
                    <img
                      src={item.course.thumbnail_url}
                      alt={item.course.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2 p-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {item.course.category && (
                      <Badge variant="secondary" className="text-[10px]">
                        {item.course.category}
                      </Badge>
                    )}
                    {item.course.level && (
                      <Badge variant="outline" className="text-[10px]">
                        {item.course.level}
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold leading-snug line-clamp-2">
                    {item.course.title}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.reason}</p>
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="text-sm font-semibold tabular-nums">
                      {item.course.is_free
                        ? "Grátis"
                        : item.course.price
                          ? fmt(Number(item.course.price))
                          : "—"}
                    </span>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                      Ver curso
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
